/**
 * Error classification — *why* a turn failed, as a bounded enum.
 *
 * A failure that reaches telemetry as nothing but a display string cannot be
 * attributed: "the add-in is failing" stays unanswerable because expired API
 * keys, a rate limit, a sick LibreChat instance and a corporate proxy all look
 * identical (issue #48). The kind is what separates them:
 *
 * | Spike       | Reading                                                  |
 * | ----------- | -------------------------------------------------------- |
 * | `http_401`  | Expired or wrong API keys — config, not an outage        |
 * | `http_429`  | Rate-limited — a capacity conversation, not a bug        |
 * | `http_5xx`  | The LibreChat instance is sick — not our add-in          |
 * | `timeout`   | Model too slow or context too large                      |
 * | `network`   | Proxy/VPN/CORS — usually one bad environment             |
 *
 * The set is deliberately small and closed so it is safe as a metric label
 * under ADR-0002's first label rule ("bounded enumerations may be labels").
 * The raw message stays on the Tempo span, where cardinality is free.
 */

export type ErrorKind =
  | "timeout"
  | "network"
  | "http_401"
  | "http_429"
  | "http_4xx"
  | "http_5xx"
  | "stream"
  | "excel"
  | "unknown";

/**
 * Every kind, as data, so the set can be asserted on and used to reject
 * anything outside it. Mirrored in `otel.js`, which cannot import this
 * module and must not trust the wire.
 */
export const ERROR_KINDS: readonly ErrorKind[] = [
  "timeout",
  "network",
  "http_401",
  "http_429",
  "http_4xx",
  "http_5xx",
  "stream",
  "excel",
  "unknown",
];

/**
 * Maps an HTTP status onto a kind. Statuses worth their own line get one;
 * everything else falls into the 4xx/5xx bucket rather than becoming a label
 * value of its own, which would let a misbehaving upstream mint new time
 * series at will.
 */
export function httpErrorKind(status: number): ErrorKind {
  if (status === 401) return "http_401";
  if (status === 429) return "http_429";
  if (status >= 500) return "http_5xx";
  if (status >= 400) return "http_4xx";
  // Not an error status at all — whatever went wrong, the code doesn't say.
  return "unknown";
}

/** An error that knows why it happened, not just how to describe itself. */
export class ClassifiedError extends Error {
  readonly kind: ErrorKind;

  constructor(kind: ErrorKind, message: string) {
    super(message);
    this.name = "ClassifiedError";
    this.kind = kind;
    // Subclassing a built-in breaks the prototype chain when down-levelled
    // (the project targets ES2020), which would make `instanceof` here false.
    Object.setPrototypeOf(this, ClassifiedError.prototype);
  }
}

/**
 * The kind of whatever was thrown. Anything that didn't classify itself is
 * `"unknown"` — including an error carrying a `kind` outside the set, since
 * a label value is only bounded if nothing can widen it from the outside.
 */
export function classifyError(err: unknown): ErrorKind {
  const kind =
    typeof err === "object" && err !== null
      ? (err as {kind?: unknown}).kind
      : undefined;

  return ERROR_KINDS.includes(kind as ErrorKind)
    ? (kind as ErrorKind)
    : "unknown";
}

/** The message to put on the span: what was thrown, however it was thrown. */
export function errorMessageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * True for the rejection an `AbortSignal` produces, whoever raised it — the
 * one throw that is not a failure, since somebody asked for it. Matched on the
 * name alone because the real one is a `DOMException`, whose relationship to
 * `Error` differs between the browser and Node.
 */
export function isAbortError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as {name?: unknown}).name === "AbortError"
  );
}
